import { inject, Injectable } from '@angular/core';
import { HttpStatusCode } from '@angular/common/http';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, exhaustMap, map, of, switchMap, withLatestFrom } from 'rxjs';
import { listDeletedParams, listResultActions$ } from '@shared/utils/pagination.utils';
import { TagDto } from '@shared/dto';
import { TagService } from '../services/tag.service';
import * as TagActions from './tag.actions';
import { selectTagState } from './tag.selectors';

@Injectable()
export class TagEffects {
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);
  private readonly tagService = inject(TagService);

  readTags$ = createEffect(() => this.actions$.pipe(
    ofType(TagActions.readTags),
    exhaustMap(({ query }) =>
      listResultActions$(
        this.tagService.read(undefined, { params: listDeletedParams(query?.deleted) }),
        undefined,
        ({ data, total }) => TagActions.readTagsSuccess({ data, total }),
        (error) => TagActions.readTagsFailure({ error }),
      )),
  ));

  readTag$ = createEffect(() => this.actions$.pipe(
    ofType(TagActions.readTag),
    exhaustMap(({ tagId }) => this.tagService.readOne({ tagId }).pipe(
      map((tag: TagDto) => TagActions.readTagSuccess({ tag })),
      catchError((error) => of(TagActions.readTagFailure({ error }))),
    )),
  ));

  createTag$ = createEffect(() => this.actions$.pipe(
    ofType(TagActions.createTag),
    exhaustMap(({ tag }) => this.tagService.create(tag).pipe(
      map((created: TagDto) => TagActions.createTagSuccess({ tag: created })),
      catchError((error) => of(TagActions.createTagFailure({ error }))),
    )),
  ));

  updateTag$ = createEffect(() => this.actions$.pipe(
    ofType(TagActions.updateTag),
    // `update` is typed Partial<Dto> by the base service; the tags endpoint
    // answers the whole row.
    exhaustMap(({ tagId, tag }) => this.tagService.update({ tagId }, tag).pipe(
      map((updated) => TagActions.updateTagSuccess({ tag: updated as TagDto })),
      catchError((error) => of(TagActions.updateTagFailure({ error }))),
    )),
  ));

  deleteTag$ = createEffect(() => this.actions$.pipe(
    ofType(TagActions.deleteTag),
    exhaustMap(({ tagId, confirm }) => this.tagService.delete(
      { tagId },
      confirm ? { params: { confirm: 'true' } } : undefined,
    ).pipe(
      map(() => TagActions.deleteTagSuccess({ tagId })),
      catchError((error) => {
        // 428 = the tag is still on exercises. Not a failure: the page asks, and
        // resends with `confirm=true`. The references are fetched here rather
        // than carried in the error, which by design says only that a decision
        // is needed — so the prompt can name the exercises instead of a count.
        if (error?.status === HttpStatusCode.PreconditionRequired) {
          return this.tagService.readReferences(tagId).pipe(
            map((references) => TagActions.deleteTagRequiresConfirmation({
              tagId,
              message: error.message,
              references,
            })),
            // The confirmation must not depend on that second read succeeding:
            // without it the user simply gets the message without the list.
            catchError(() => of(TagActions.deleteTagRequiresConfirmation({
              tagId,
              message: error.message,
              references: null,
            }))),
          );
        }
        return of(TagActions.deleteTagFailure({ error }));
      }),
    )),
  ));

  restoreTag$ = createEffect(() => this.actions$.pipe(
    ofType(TagActions.restoreTag),
    exhaustMap(({ tagId }) => this.tagService.restore({ tagId }).pipe(
      map((tag: TagDto) => TagActions.restoreTagSuccess({ tag })),
      catchError((error) => of(TagActions.restoreTagFailure({ error }))),
    )),
  ));

  /**
   * Every write reloads the list. The table is small and unpaginated, so a
   * refetch is cheaper than keeping a local copy in step — and it is the only
   * thing that shows a rename on the exercises that carry the tag.
   */
  reload$ = createEffect(() => this.actions$.pipe(
    ofType(
      TagActions.createTagSuccess,
      TagActions.updateTagSuccess,
      TagActions.deleteTagSuccess,
      TagActions.restoreTagSuccess,
    ),
    withLatestFrom(this.store.select(selectTagState)),
    switchMap(([, state]) => of(TagActions.readTags(state.query ?? undefined))),
  ));
}
